import { AxiosResponse } from "axios";
import { axiosRequest } from "services/api/api.utils";
import { DOCUMENT_API_ROOT } from "services/baseURLs.config";

export interface Participant {
  externalId: string;
  ssn: string; // 12-digit Swedish Personal Number (regex: ^[0-9]{12}$)
  name: string; // minimum length: 1
  email: string; // minimum length: 1
  metadata?: Record<string, any>;
}

export interface DocumentCallback {
  url: string;
  path?: string;
}

export interface CreateDocumentRequest {
  file?: File | Blob;
  participants: Participant[];
  initiator: string;
  documentName?: string;
  category?: string;
  contractType?: string;
  callback?: DocumentCallback;
}

export interface CreateDocumentResponse {
  id: string;
  participants: {
    id: string;
    status: string;
  }[];
}

export interface GetDocumentResponseParticipant {
  id: string;
  name: string;
  status: "unsigned" | "signed" | string;
  signedAt: string | null;
}

export interface GetDocumentResponse {
  id: string;
  status: "created" | "pending" | "completed" | "expired" | string;
  documentName: string | null;
  isImported?: boolean;
  category?: string | null;
  contractType?: string | null;
  created_at?: string | null;
  expires_at?: string | null;
  participants: GetDocumentResponseParticipant[];
}

const createDocument = async (
  data: CreateDocumentRequest,
  queryParams?: { companyId?: string; companyUserId?: string },
): Promise<AxiosResponse<CreateDocumentResponse>> => {
  const formData = new FormData();
  if (data.file) {
    const filename = (data.file as File).name || "agreement.pdf";
    formData.append("file", data.file, filename);
  }
  formData.append("initiator", data.initiator);
  if (data.documentName) {
    formData.append("documentName", data.documentName);
  }
  if (data.category) {
    formData.append("category", data.category);
  }
  if (data.contractType) {
    formData.append("contractType", data.contractType);
  }
  formData.append("participants", JSON.stringify(data.participants));
  if (data.callback) {
    formData.append("callback", JSON.stringify(data.callback));
  }

  return await axiosRequest.auth.post<CreateDocumentResponse>(
    `${DOCUMENT_API_ROOT}/create`,
    formData,
    {
      params: queryParams,
    },
  );
};

const getDocument = async (
  id: string,
  queryParams?: {
    workerId?: string;
    companyId?: string;
    companyUserId?: string;
  },
): Promise<AxiosResponse<GetDocumentResponse>> => {
  return await axiosRequest.auth.get<GetDocumentResponse>(
    `${DOCUMENT_API_ROOT}/${id}`,
    {
      params: queryParams,
    },
  );
};

const getOriginalPdf = async (id: string): Promise<AxiosResponse<Blob>> => {
  return await axiosRequest.auth.get<Blob>(`${DOCUMENT_API_ROOT}/${id}/pdf`, {
    responseType: "blob",
  });
};

const getSignedPdf = async (
  id: string,
  queryParams?: {
    workerId?: string;
    companyId?: string;
    companyUserId?: string;
  },
): Promise<AxiosResponse<Blob>> => {
  return await axiosRequest.auth.get<Blob>(
    `${DOCUMENT_API_ROOT}/${id}/signed-pdf`,
    {
      params: queryParams,
      responseType: "blob",
    },
  );
};

export interface ImportParticipant {
  externalId: string;
  ssn?: string;
  name?: string;
  email?: string;
  signedAt?: string;
  metadata?: {
    type: "company" | "worker" | string;
    companyId?: number;
    workerId?: number;
    userId?: number;
    [key: string]: any;
  };
}

export interface ImportDocumentRequest {
  file: File | Blob;
  initiator: string;
  participants?: ImportParticipant[];
  documentName?: string;
  category?: string;
  contractType?: string;
  notes?: string;
  callback?: DocumentCallback;
}

export interface ImportDocumentResponse {
  id: string;
  status: "completed" | string;
  isImported?: boolean;
  category?: string;
  contractType?: string;
  participants?: {
    id: string;
    name: string | null;
    status: string;
    signedAt?: string | null;
  }[];
}

const importDocument = async (
  data: ImportDocumentRequest,
  queryParams?: { companyId?: string | number; companyUserId?: string | number },
): Promise<AxiosResponse<ImportDocumentResponse>> => {
  const formData = new FormData();
  const filename = (data.file as File).name || "agreement.pdf";
  formData.append("file", data.file, filename);
  formData.append("initiator", data.initiator);
  if (data.documentName) {
    formData.append("documentName", data.documentName);
  }
  if (data.category) {
    formData.append("category", data.category);
  }
  if (data.contractType) {
    formData.append("contractType", data.contractType);
  }
  if (data.notes) {
    formData.append("notes", data.notes);
  }
  if (data.participants && data.participants.length > 0) {
    formData.append("participants", JSON.stringify(data.participants));
  }
  if (data.callback) {
    formData.append("callback", JSON.stringify(data.callback));
  }

  return await axiosRequest.auth.post<ImportDocumentResponse>(
    `${DOCUMENT_API_ROOT}/import`,
    formData,
    {
      params: queryParams,
    },
  );
};

export const document = {
  createDocument,
  importDocument,
  getDocument,
  getOriginalPdf,
  getSignedPdf,
};
